import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { UploadFile as UploadIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import imputableService from '../../../services/imputableService';
import noImputableService from '../../../services/noImputableService';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  buildPlanCuentasPreview,
  parsePlanCuentasExcelFile,
  type PlanCuentasAccion,
  type PlanCuentasPreviewFila,
} from '../../../utils/planCuentasImportParse';

interface PlanCuentasImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

type Step = 'origen' | 'preview' | 'done';

const PlanCuentasImportDialog: React.FC<PlanCuentasImportDialogProps> = ({
  open,
  onClose,
  onImported,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('origen');
  const [preview, setPreview] = useState<PlanCuentasPreviewFila[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [resultado, setResultado] = useState<{
    creados: number;
    actualizados: number;
    omitidos: number;
    errores: { fila: number; mensaje: string }[];
  } | null>(null);

  const reset = () => {
    setStep('origen');
    setPreview([]);
    setParseErrors([]);
    setLoading(false);
    setProgress(0);
    setResultado(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setLoading(true);
    try {
      const parsed = await parsePlanCuentasExcelFile(file);
      const localErrors = parsed.errores.map((e) =>
        e.fila ? `Fila ${e.fila}: ${e.mensaje}` : e.mensaje
      );

      if (!parsed.filas.length) {
        setParseErrors(localErrors.length ? localErrors : ['No hay filas para importar.']);
        setPreview([]);
        setStep('preview');
        return;
      }

      const [imps, noImps] = await Promise.all([
        imputableService.getAllImputablesPlanCuentas(),
        noImputableService.getAllNoImputablesPlanCuentas(),
      ]);

      const setImp = new Set(imps.map((i) => String(i.codImputable)));
      const setNoImp = new Set(noImps.map((n) => n.nroRubro));
      const rows = buildPlanCuentasPreview(parsed.filas, setImp, setNoImp);
      setPreview(rows);
      setParseErrors(localErrors);
      setStep('preview');
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo leer el archivo'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const setAccionFila = (fila: number, accion: PlanCuentasAccion) => {
    setPreview((prev) =>
      prev.map((r) => (r.fila === fila && r.estado === 'Existente' ? { ...r, accion } : r))
    );
  };

  const applyAccionMasiva = (accion: PlanCuentasAccion) => {
    setPreview((prev) =>
      prev.map((r) => (r.estado === 'Existente' ? { ...r, accion } : r))
    );
  };

  const handleConfirm = async () => {
    const aProcesar = preview.filter((f) => {
      if (f.estado === 'Error') return false;
      if (f.estado === 'Nuevo') return true;
      return f.accion === 'Actualizar';
    });

    const omitidosBase = preview.filter(
      (f) => f.estado === 'Existente' && f.accion === 'Omitir'
    ).length;

    if (aProcesar.length === 0 && omitidosBase === 0) {
      enqueueSnackbar('No hay filas válidas para importar', { variant: 'warning' });
      return;
    }

    setLoading(true);
    setProgress(0);
    let creados = 0;
    let actualizados = 0;
    let omitidos = omitidosBase;
    const errores: { fila: number; mensaje: string }[] = [];

    // Primero no imputables (rubros), luego imputables (dependen del rubro).
    const ordenados = [
      ...aProcesar.filter((f) => f.destino === 'no_imputable'),
      ...aProcesar.filter((f) => f.destino === 'imputable'),
    ];

    for (let i = 0; i < ordenados.length; i++) {
      const f = ordenados[i];
      try {
        if (f.destino === 'no_imputable') {
          const payload = {
            nroRubro: Number(f.codigo),
            descripcioncta: f.nombre,
            nombreCuenta: f.nombre,
            segmento: f.nivel,
            totalizaEn: f.totalizaEn,
            saldo: 0,
          };
          if (f.estado === 'Nuevo') {
            await noImputableService.createNoImputable(payload);
            creados += 1;
          } else {
            await noImputableService.updateNoImputable(Number(f.codigo), payload);
            actualizados += 1;
          }
        } else {
          const payload = {
            codImputable: f.codigo,
            nombre: f.nombre,
            nroRubro: f.nroRubro,
            imputable: 'S',
            tipo: f.tipo,
            activo: 'S',
            visibleEn: f.visibleEn,
            descripcion: f.descripcion,
          };
          if (f.estado === 'Nuevo') {
            await imputableService.createImputable(payload);
            creados += 1;
          } else {
            await imputableService.updateImputable(f.codigo, payload);
            actualizados += 1;
          }
        }
      } catch (err) {
        errores.push({
          fila: f.fila,
          mensaje: getApiErrorMessage(err, 'Error al procesar fila'),
        });
        omitidos += 1;
      }
      setProgress(Math.round(((i + 1) / Math.max(ordenados.length, 1)) * 100));
    }

    setResultado({ creados, actualizados, omitidos, errores });
    setStep('done');
    enqueueSnackbar(
      `Importación: ${creados} creados, ${actualizados} actualizados, ${omitidos} omitidos`,
      { variant: errores.length ? 'warning' : 'success' }
    );
    onImported();
    setLoading(false);
  };

  const estadoChip = (estado: string) => {
    if (estado === 'Existente') return <Chip size="small" label="Existente" color="warning" />;
    if (estado === 'Error') return <Chip size="small" label="Error" color="error" />;
    return <Chip size="small" label="Nuevo" color="success" />;
  };

  const destinoChip = (destino: string) =>
    destino === 'no_imputable' ? (
      <Chip size="small" label="No imputable" variant="outlined" />
    ) : (
      <Chip size="small" label="Imputable" variant="outlined" color="primary" />
    );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 600 }}>Importar plan de cuentas</DialogTitle>
      <DialogContent dividers>
        {step === 'origen' && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Subí el Excel exportado (misma plantilla para imputables y no imputables). Columnas:
              Código, Nombre, Nro. rubro, No imputable, Imputable (Sí/No), Tipo de cuenta, Visible
              en, Nivel, Totaliza en. Si Imputable = No, la fila se trata como rubro (no imputable).
            </Typography>
            <Box>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => fileRef.current?.click()}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                Subir archivo
              </Button>
              <input
                ref={fileRef}
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={(e) => void handleFile(e.target.files?.[0] ?? null)}
              />
            </Box>
            {loading && <LinearProgress />}
          </Stack>
        )}

        {step === 'preview' && (
          <Stack spacing={2}>
            {parseErrors.length > 0 && (
              <Alert severity="warning">
                {parseErrors.map((e) => (
                  <div key={e}>{e}</div>
                ))}
              </Alert>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Existentes:
              </Typography>
              <Button size="small" onClick={() => applyAccionMasiva('Omitir')} sx={{ textTransform: 'none' }}>
                Omitir todos
              </Button>
              <Button
                size="small"
                onClick={() => applyAccionMasiva('Actualizar')}
                sx={{ textTransform: 'none' }}
              >
                Actualizar todos
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {preview.filter((p) => p.estado === 'Nuevo').length} nuevos ·{' '}
                {preview.filter((p) => p.estado === 'Existente').length} existentes ·{' '}
                {preview.filter((p) => p.destino === 'no_imputable').length} no imputables ·{' '}
                {preview.filter((p) => p.destino === 'imputable').length} imputables
              </Typography>
            </Stack>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Fila</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Destino</TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Nro. rubro / Nivel</TableCell>
                    <TableCell>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.map((f) => (
                    <TableRow key={f.fila}>
                      <TableCell>{f.fila}</TableCell>
                      <TableCell>{estadoChip(f.estado)}</TableCell>
                      <TableCell>{destinoChip(f.destino)}</TableCell>
                      <TableCell>{f.codigo}</TableCell>
                      <TableCell>{f.nombre}</TableCell>
                      <TableCell>
                        {f.destino === 'imputable'
                          ? `Rubro ${f.nroRubro}`
                          : `Nivel ${f.nivel}${f.totalizaEn ? ` · Tot. ${f.totalizaEn}` : ''}`}
                      </TableCell>
                      <TableCell>
                        {f.estado === 'Existente' ? (
                          <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                              {f.mensaje}
                            </Typography>
                            <TextField
                              select
                              size="small"
                              value={f.accion}
                              onChange={(e) =>
                                setAccionFila(f.fila, e.target.value as PlanCuentasAccion)
                              }
                              sx={{ minWidth: 140 }}
                            >
                              <MenuItem value="Omitir">Omitir</MenuItem>
                              <MenuItem value="Actualizar">Actualizar</MenuItem>
                            </TextField>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {f.mensaje ?? 'Se creará'}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {loading && (
              <Box>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="caption" color="text.secondary">
                  Procesando… {progress}%
                </Typography>
              </Box>
            )}
          </Stack>
        )}

        {step === 'done' && resultado && (
          <Stack spacing={1.5}>
            <Alert severity={resultado.errores.length ? 'warning' : 'success'}>
              Importación finalizada: {resultado.creados} creados, {resultado.actualizados}{' '}
              actualizados, {resultado.omitidos} omitidos.
            </Alert>
            {resultado.errores.length > 0 && (
              <Alert severity="warning">
                {resultado.errores.map((e) => (
                  <div key={`${e.fila}-${e.mensaje}`}>
                    Fila {e.fila}: {e.mensaje}
                  </div>
                ))}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === 'preview' && (
          <Button
            onClick={() => {
              setStep('origen');
              setPreview([]);
            }}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            Volver
          </Button>
        )}
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
          {step === 'done' ? 'Cerrar' : 'Cancelar'}
        </Button>
        {step === 'preview' && (
          <Button
            variant="contained"
            disabled={loading || preview.length === 0}
            onClick={() => void handleConfirm()}
            sx={{ textTransform: 'none' }}
          >
            Confirmar importación
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PlanCuentasImportDialog;
